import { useState } from 'react';
import { Badge, Button, Group, Select, Stack } from '@mantine/core';
import { IconListSearch } from '@tabler/icons-react';
import { MOVEMENT_TYPES } from '@zebra/core';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { LocationFilterSelect, ProductFilterSelect, SiteFilterSelect } from '../components/FilterSelects';
import { ReportPage } from '../components/ReportPage';
import { ReportTable, type ReportColumn } from '../components/ReportTable';
import { rangeQuery, useMovementLedgerReport } from '../api';
import { formatDateTime, formatKg, formatMoneyCents, formatQty } from '../format';
import { MOVEMENT_TYPE_LABELS } from '../../../lib/enumLabels';
import type { MovementRow } from '../types';

// Human labels for `MovementType` (Item 5a) — plant/office staff never see the raw
// enum tokens (`PRODUCTION_OUT`, `PACK_IN`, ...) in the filter dropdown or the
// badge. Moved to lib/enumLabels.ts (guidance wave, 2026-08-25) so the same map
// is available to other screens that render movement types. Keeps the raw value
// as the filter option's `value` (server still filters on it).
const TYPE_OPTIONS = MOVEMENT_TYPES.map((t) => ({ value: t, label: MOVEMENT_TYPE_LABELS[t] }));

/** Report #2 + register "the ledger browser" (BUILD_SPEC.md §13) — the one
 * genuinely high-volume report, so pagination is cursor-based "Load more" rather
 * than a single page (mirrors services/api's own cursor contract). */
export function MovementLedgerReport() {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursors[cursors.length - 1];

  const query = useMovementLedgerReport({
    siteId: siteId ?? undefined,
    locationId: locationId ?? undefined,
    productId: productId ?? undefined,
    type: type ?? undefined,
    from,
    to,
    cursor,
    limit: 50,
  });
  const rows = query.data?.items ?? [];

  function resetPaging() {
    setCursors([undefined]);
  }

  const columns: ReportColumn<MovementRow>[] = [
    { key: 'occurredAt', header: 'Occurred', render: (r) => formatDateTime(r.occurredAt) },
    { key: 'type', header: 'Type', render: (r) => <Badge variant="light">{MOVEMENT_TYPE_LABELS[r.type]}</Badge> },
    { key: 'product', header: 'Product', render: (r) => `${r.productSku} — ${r.productName}` },
    { key: 'location', header: 'Location', render: (r) => r.locationCode },
    { key: 'qty', header: 'Qty', align: 'right', render: (r) => formatQty(r.qty) },
    { key: 'dryKg', header: 'Dry kg', align: 'right', render: (r) => formatKg(r.dryKg) },
    { key: 'value', header: 'Value', align: 'right', render: (r) => formatMoneyCents(r.valueCents) },
    { key: 'reference', header: 'Reference', render: (r) => `${r.referenceType} ${r.referenceId.slice(-6)}` },
    { key: 'source', header: 'Source', render: (r) => r.source },
    { key: 'createdBy', header: 'Created by', render: (r) => r.createdByName ?? '—' },
  ];

  return (
    <ReportPage
      title="Movement ledger browser"
      description="Every posted stock movement (the append-only ledger), filterable and paginated."
      action={
        <CsvDownloadButton
          path="/reporting/movements"
          query={{
            siteId: siteId ?? undefined,
            locationId: locationId ?? undefined,
            productId: productId ?? undefined,
            type: type ?? undefined,
            ...rangeQuery(from, to),
          }}
          filename="movement-ledger.csv"
          description="Exports every movement matching your current filters as a CSV file"
        />
      }
    >
      <Stack gap="md">
        <Group gap="md" wrap="wrap" align="flex-end">
          <SiteFilterSelect
            value={siteId}
            onChange={(v) => {
              setSiteId(v);
              resetPaging();
            }}
            label="Site"
            w={200}
          />
          <LocationFilterSelect
            value={locationId}
            onChange={(v) => {
              setLocationId(v);
              resetPaging();
            }}
            siteId={siteId ?? undefined}
            label="Location"
            w={200}
          />
          <ProductFilterSelect
            value={productId}
            onChange={(v) => {
              setProductId(v);
              resetPaging();
            }}
            label="Product"
            w={220}
          />
          <Select
            label="Type"
            placeholder="All types"
            clearable
            data={TYPE_OPTIONS}
            value={type}
            onChange={(v) => {
              setType(v);
              resetPaging();
            }}
            w={180}
            comboboxProps={{ withinPortal: true }}
          />
          <DateRangeFilter
            from={from}
            to={to}
            onFromChange={(v) => {
              setFrom(v);
              resetPaging();
            }}
            onToChange={(v) => {
              setTo(v);
              resetPaging();
            }}
          />
        </Group>

        <ReportTable
          columns={columns}
          rows={rows}
          getRowKey={(r) => r.id}
          isLoading={query.isLoading}
          isError={query.isError}
          errorMessage="Failed to load the movement ledger."
          emptyMessage="No movements for the selected filters"
          emptyHint="Try widening the date range or clearing a filter."
          emptyIcon={IconListSearch}
        />

        <Group justify="space-between">
          <Button
            variant="default"
            size="sm"
            disabled={cursors.length <= 1}
            onClick={() => setCursors((prev) => prev.slice(0, -1))}
          >
            Previous page
          </Button>
          <Button
            variant="default"
            size="sm"
            disabled={!query.data?.nextCursor}
            onClick={() => setCursors((prev) => [...prev, query.data?.nextCursor ?? undefined])}
          >
            Next page
          </Button>
        </Group>
      </Stack>
    </ReportPage>
  );
}
